import { useEffect, useLayoutEffect, useState, useCallback, createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  generateBrandTheme,
  hexToOklch,
  THEME_PRESETS,
  DEFAULT_PRESET,
  DEFAULT_MODE,
  buildPresetCss,
  isThemeMode,
  isThemePresetKey,
  type BrandTheme,
  type ThemeMode,
  type ThemePresetKey,
} from '@erp/config';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import { BrandBootBlank } from '@/components/BrandSplash';
import { DEFAULT_BRAND_FAVICON } from '@/lib/default-brand';
import { SETTINGS_KEY, SETTINGS_QUERY_OPTIONS } from '@/hooks/use-settings';
import { isPlatformAdminMode } from '@/lib/platform-admin';
import {
  hasActivePlatformAdminTenantViewAccess,
  hasPlatformAdminTenantHandoffIntent,
} from '@/lib/admin-tenant-view';

const LS_MODE_KEY = 'erp-theme-mode';
const LS_PRESET_KEY = 'erp-theme-preset';
const LS_SIDEBAR_THEME_KEY = 'erp-sidebar-theme';

export type SidebarTheme = 'dark' | 'light';

function isSidebarTheme(v: unknown): v is SidebarTheme {
  return v === 'dark' || v === 'light';
}

function readCachedSidebarTheme(): SidebarTheme {
  if (typeof window === 'undefined') return 'dark';
  const raw = window.localStorage.getItem(LS_SIDEBAR_THEME_KEY);
  return isSidebarTheme(raw) ? raw : 'dark';
}

interface BrandThemeContextValue {
  primaryColor: string;
  theme: BrandTheme;
  setPrimaryColor: (hex: string) => void;
  borderRadius: string;
  setBorderRadius: (r: string) => void;
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  resolvedMode: 'light' | 'dark';
  preset: ThemePresetKey;
  setPreset: (p: ThemePresetKey) => void;
  sidebarTheme: SidebarTheme;
  setSidebarTheme: (s: SidebarTheme) => void;
}

const BrandThemeContext = createContext<BrandThemeContextValue | null>(null);

// Default primary aligns with the public Gesty landing page.
const DEFAULT_PRIMARY = '#1f6fff';
const DEFAULT_RADIUS = '0.625';
const BRAND_BOOT_TIMEOUT_MS = 7000;

function readSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readCachedMode(): ThemeMode {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  const raw = window.localStorage.getItem(LS_MODE_KEY);
  return isThemeMode(raw) ? raw : DEFAULT_MODE;
}

function readCachedPreset(): ThemePresetKey {
  if (typeof window === 'undefined') return DEFAULT_PRESET;
  const raw = window.localStorage.getItem(LS_PRESET_KEY);
  return isThemePresetKey(raw) ? raw : DEFAULT_PRESET;
}

interface BrandThemeProviderProps {
  children: ReactNode;
  initialPrimaryColor?: string;
  initialBorderRadius?: string;
}

export function BrandThemeProvider({
  children,
  initialPrimaryColor = DEFAULT_PRIMARY,
  initialBorderRadius = DEFAULT_RADIUS,
}: BrandThemeProviderProps) {
  const ds = useDataService();
  const { user, isLoading: authLoading } = useAuth();
  const adminTenantViewAccess = hasActivePlatformAdminTenantViewAccess();
  const adminTenantHandoffIntent = hasPlatformAdminTenantHandoffIntent();
  const platformAdminMode = isPlatformAdminMode() && !adminTenantViewAccess;
  const [primaryColor, setPrimaryColor] = useState(initialPrimaryColor);
  const [borderRadius, setBorderRadius] = useState(initialBorderRadius);
  const [mode, setModeState] = useState<ThemeMode>(() => readCachedMode());
  const [preset, setPresetState] = useState<ThemePresetKey>(() => readCachedPreset());
  const [sidebarTheme, setSidebarThemeState] = useState<SidebarTheme>(() => readCachedSidebarTheme());
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => readSystemPrefersDark());
  const [brandReady, setBrandReady] = useState(false);
  const [theme, setTheme] = useState<BrandTheme>(() =>
    generateBrandTheme(initialPrimaryColor),
  );

  const resolvedMode: 'light' | 'dark' =
    mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;

  const settingsQuery = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => withBrandBootTimeout(ds.settings.getCompanySettings()),
    ...SETTINGS_QUERY_OPTIONS,
    enabled: (!!user || adminTenantViewAccess) && !platformAdminMode,
    networkMode: 'always',
  });
  const { data: settings } = settingsQuery;

  // Reset when user changes (logout/login)
  useEffect(() => {
    if (adminTenantViewAccess) {
      return;
    }
    setBrandReady(!user && !authLoading);
    document.documentElement.removeAttribute('data-brand-ready');
  }, [adminTenantViewAccess, authLoading, user?.id]);

  useEffect(() => {
    if (!user || authLoading || adminTenantViewAccess || platformAdminMode || brandReady) {
      return;
    }
    const timeout = window.setTimeout(() => {
      console.warn('[branding] boot guard timed out, continuing with neutral chrome');
      document.documentElement.removeAttribute('data-brand-ready');
      setBrandReady(true);
    }, BRAND_BOOT_TIMEOUT_MS + 1000);
    return () => window.clearTimeout(timeout);
  }, [adminTenantViewAccess, authLoading, brandReady, platformAdminMode, user?.id]);

  useLayoutEffect(() => {
    if (authLoading && !adminTenantHandoffIntent) {
      setBrandReady(false);
      document.documentElement.removeAttribute('data-brand-ready');
      return;
    }

    if (!user && !adminTenantViewAccess) {
      setBrandReady(true);
      document.documentElement.removeAttribute('data-brand-ready');
      return;
    }

    if (platformAdminMode) {
      document.documentElement.setAttribute('data-brand-ready', 'true');
      setBrandReady(true);
      return;
    }

    if (settingsQuery.isError) {
      // 'not_in_tenant' is the expected state for users who have just
      // signed up and haven't completed the OnboardingWizard yet — they
      // have an auth.users row but no tenant_member, so the RPC returns
      // null. The wizard resolves this the moment it submits. Don't spam
      // the console with warnings for that case; only log genuinely
      // unexpected errors (network, RLS, server, …).
      const err = settingsQuery.error;
      const isExpectedDuringOnboarding =
        err instanceof Error && err.message === 'not_in_tenant';
      if (!isExpectedDuringOnboarding) {
        console.warn('[branding] settings check failed, continuing with neutral chrome', err);
      }
      setBrandReady(true);
      document.documentElement.removeAttribute('data-brand-ready');
      return;
    }

    if (!settingsQuery.isSuccess) {
      setBrandReady(false);
      return;
    }
    if (settings?.primaryColor) setPrimaryColor(settings.primaryColor);
    if (settings?.borderRadius) setBorderRadius(settings.borderRadius);
    if (isThemeMode(settings?.themeMode)) setModeState(settings.themeMode);
    if (isThemePresetKey(settings?.themePreset)) setPresetState(settings.themePreset);
    const faviconHref = settings?.faviconUrl || DEFAULT_BRAND_FAVICON;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      document.head.appendChild(link);
    }
    link.href = faviconHref;
    document.documentElement.setAttribute('data-brand-ready', 'true');
    setBrandReady(true);
  }, [
    adminTenantHandoffIntent,
    adminTenantViewAccess,
    authLoading,
    platformAdminMode,
    settings,
    settingsQuery.error,
    settingsQuery.isError,
    settingsQuery.isSuccess,
    user,
  ]);

  useLayoutEffect(() => {
    setTheme(generateBrandTheme(primaryColor));
  }, [primaryColor]);

  // Listen to system color-scheme changes when mode === 'system'
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Toggle .dark class on <html>
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedMode === 'dark');
  }, [resolvedMode]);

  // Sidebar theme — data attribute on <html> so CSS can opt the sidebar
  // into a light surface (the default :root values are dark). 'dark' is
  // the default and matches the :root tokens directly, so no attribute.
  useLayoutEffect(() => {
    if (sidebarTheme === 'dark') {
      document.documentElement.removeAttribute('data-sidebar-theme');
    } else {
      document.documentElement.setAttribute('data-sidebar-theme', 'light');
    }
  }, [sidebarTheme]);

  // Apply preset scale (both light and dark rules) via a managed <style> tag
  useLayoutEffect(() => {
    const styleId = 'brand-theme-preset';
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    const p = THEME_PRESETS[preset];
    style.textContent = `${buildPresetCss(':root', p.light)}\n${buildPresetCss('.dark', p.dark)}`;
  }, [preset]);

  // Inject per-client primary color on top of preset (wins via specificity)
  useLayoutEffect(() => {
    const root = document.documentElement;

    root.style.setProperty('--primary', `oklch(${hexToOklch(theme.light.primary)})`);
    root.style.setProperty('--primary-foreground', `oklch(${hexToOklch(theme.light.primaryForeground)})`);
    root.style.setProperty('--ring', `oklch(${hexToOklch(theme.light.ring)})`);
    root.style.setProperty('--sidebar-primary', `oklch(${hexToOklch(theme.light.primary)})`);
    root.style.setProperty('--sidebar-primary-foreground', `oklch(${hexToOklch(theme.light.primaryForeground)})`);

    // Border radius (shadcn's official customization variable)
    const radiusNum = parseFloat(borderRadius);
    if (!isNaN(radiusNum)) {
      root.style.setProperty('--radius', `${radiusNum}rem`);
    }

    // Dark primary via dedicated style tag (inline style can't target .dark)
    const darkStyleId = 'brand-theme-dark';
    let darkStyle = document.getElementById(darkStyleId) as HTMLStyleElement | null;
    if (!darkStyle) {
      darkStyle = document.createElement('style');
      darkStyle.id = darkStyleId;
      document.head.appendChild(darkStyle);
    }
    darkStyle.textContent = `
      .dark {
        --primary: oklch(${hexToOklch(theme.dark.primary)});
        --primary-foreground: oklch(${hexToOklch(theme.dark.primaryForeground)});
        --ring: oklch(${hexToOklch(theme.dark.ring)});
        --sidebar-primary: oklch(${hexToOklch(theme.dark.primary)});
        --sidebar-primary-foreground: oklch(${hexToOklch(theme.dark.primaryForeground)});
      }
    `;
  }, [theme, borderRadius]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    if (typeof window !== 'undefined') window.localStorage.setItem(LS_MODE_KEY, m);
  }, []);

  const setPreset = useCallback((p: ThemePresetKey) => {
    setPresetState(p);
    if (typeof window !== 'undefined') window.localStorage.setItem(LS_PRESET_KEY, p);
    setPrimaryColor(THEME_PRESETS[p].primaryHex);
  }, []);

  const setSidebarTheme = useCallback((s: SidebarTheme) => {
    setSidebarThemeState(s);
    if (typeof window !== 'undefined') window.localStorage.setItem(LS_SIDEBAR_THEME_KEY, s);
  }, []);

  const isBrandBooting =
    (authLoading && !adminTenantHandoffIntent) ||
    (!!user && !adminTenantViewAccess && !brandReady);

  return (
    <BrandThemeContext.Provider
      value={{
        primaryColor, theme, setPrimaryColor,
        borderRadius, setBorderRadius,
        mode, setMode, resolvedMode,
        preset, setPreset,
        sidebarTheme, setSidebarTheme,
      }}
    >
      {isBrandBooting ? <BrandBootBlank /> : children}
    </BrandThemeContext.Provider>
  );
}

function withBrandBootTimeout<T>(promise: Promise<T>): Promise<T> {
  if (typeof window === 'undefined') return promise;

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('branding settings boot timeout'));
    }, BRAND_BOOT_TIMEOUT_MS);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export function useBrandTheme(): BrandThemeContextValue {
  const ctx = useContext(BrandThemeContext);
  if (!ctx) throw new Error('useBrandTheme must be used within BrandThemeProvider');
  return ctx;
}
